import moment from "moment";

import { Base2Presenter } from "core/presenter";
import { SaleRepository } from "core/repository";

import MyFinanceService from "../my-finance.service";
import * as R from "ramda";

class MyFinancePendingTransactionsPresenter extends Base2Presenter {
  initialState() {
    return {
      loading: false,
      accountName: "",
      entityName: "",
      order: [
        {
          field: "activated_at",
          direction: "DESC"
        }
      ],
      pagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      filters: {
        startDate: moment()
          .subtract(30, "days")
          .startOf("day"),
        endDate: moment().endOf("day")
      },
      receivables: [],
      payments: [],
      checkedRows: {}
    };
  }

  async onStart() {
    await this.getAccountName();
    await this.checkPendingTransactions();
  }

  async getAccountName() {
    this.enqueue({ loading: true });
    const token = this.props.token;
    const result = await MyFinanceService.acquireAccounts(token);

    if (result.success) {
      const account_id = this.props.accountId;
      let accountName = "";

      for (let account of result.data) {
        if (account.account.id === account_id) {
          accountName = account.account.name;
        }
      }
      this.enqueue(
        { accountName },
        async () => await this.getEntityName(token, account_id)
      );
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Oops! Não foi possível resgatar a conta integrada. Verifique o token registrado e, caso necessário, edite os dados integração."
      });
      this.props.onPendingTransactionsClose();
      this.enqueue({ loading: false });
    }
  }

  async getEntityName(token, accountId) {
    const result = await MyFinanceService.acquireEntities(token, accountId);

    if (result.success) {
      const entity_id = this.props.entityId;
      let entityName = "";

      for (let entity of result.data) {
        if (entity.entity.id === parseInt(entity_id, 10)) {
          entityName = entity.entity.name;
        }
      }
      this.enqueue({ entityName });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Oops! Não foi possível resgatar a entidade integrada. Verifique o token e conta registrados e, caso necessário, edite os dados da integração."
      });
      this.props.onPendingTransactionsClose();
      this.enqueue({ loading: false });
    }
  }

  async checkPendingTransactions() {
    this.enqueue({ loading: true });
    const { order, pagination } = this.viewModel;
    const filters = await this.getFilters();
    const repositoryResult = await SaleRepository.getListOnline({
      order,
      pagination,
      filters
    });

    if (repositoryResult.success) {
      if (parseInt(repositoryResult.total, 10) === 0) {
        this.sendNotification({
          type: "success",
          content: "Não há transações pendentes."
        });
        this.props.onPendingTransactionsClose();
      } else {
        const receivableMapper = R.pathOr(
          [],
          ["processed_integrations", "my_finance"]
        );
        const mapperAmbiguous = R.pathOr(
          [],
          ["processed_integrations", "my_finance", "ambiguous_payments"]
        );
        const mapperNotFound = R.pathOr(
          [],
          ["processed_integrations", "my_finance", "not_found_payments"]
        );

        this.enqueue(vm => ({
          receivables: repositoryResult.data
            .map(s => ({
              ...s,
              processed_integrations: {
                my_finance: {
                  ...s.processed_integrations.my_finance,
                  sale_uuid: s.uuid
                }
              }
            }))
            .map(receivableMapper)
            .filter(
              r =>
                (r.ambiguous_payments && r.ambiguous_payments.length > 0) ||
                (r.not_found_payments && r.not_found_payments.length > 0)
            ),
          payments: repositoryResult.data.reduce(
            (cur, sale) => [
              ...cur,
              ...mapperAmbiguous(sale),
              ...mapperNotFound(sale)
            ],
            []
          ),
          pagination: {
            ...vm.pagination,
            total: repositoryResult.total,
            perPage: repositoryResult.perPage
          }
        }));
      }
    } else {
      this.sendNotification({
        type: "error",
        title: "Ooopss...",
        content:
          "Não foi possível carregar os pagamentos não conciliados.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
      this.props.onPendingTransactionsClose();
    }
    this.enqueue({ loading: false });
  }

  async getFilters() {
    const { startDate, endDate } = this.viewModel.filters;
    const currentUser = await this.mRuntime.get("currentUser");

    const filters = [
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: currentUser.company_uuid
      },
      {
        connector: "AND",
        field: "processed_integrations.my_finance.fully_processed",
        operation: "EQ",
        value: "false"
      }
    ];

    if (startDate) {
      filters.push({
        connector: "AND",
        field: "activated_at",
        operation: "GTE",
        value: startDate.toISOString()
      });
    }

    if (endDate) {
      filters.push({
        connector: "AND",
        field: "activated_at",
        operation: "LTE",
        value: endDate.toISOString()
      });
    }

    return filters;
  }

  async onSendTable() {
    this.enqueue({ loading: true });

    const { receivables, checkedRows } = this.viewModel;

    const paymentsFromSale = r => r.ambiguous_payments.map(p => p.payment.uuid);

    const contains = (cr, r) => {
      let bool = false;
      paymentsFromSale(r).forEach(payment => {
        if (cr.includes(payment)) {
          bool = true;
        }
      });
      return bool;
    };

    const cr = Object.entries(checkedRows)
      .filter(([_, checked]) => checked)
      .map(([payment_uuid, _]) => payment_uuid);

    const update$ = receivables
      .filter(r => !!r.ambiguous_payments[0])
      .filter(r => contains(cr, r))
      .map(async r => {
        for (const lp of r.linked_payments) {
          for (const ap of r.ambiguous_payments) {
            for (const af of ap.accounts_found) {
              if (af.sale_account.id === parseInt(lp.selected, 10)) {
                lp.selected = { sale_account: { ...af.sale_account } };
              }
            }
          }
        }

        const { sale_uuid } = r;
        delete r.sale_uuid;

        const sale = (await SaleRepository.get(sale_uuid)).data;
        return SaleRepository.update(sale_uuid, {
          ...sale,
          processed_integrations: {
            my_finance: r
          }
        });
      });

    const update = await Promise.all(update$);

    const failedUpdate = update.find(u => u.success === false);

    if (failedUpdate) {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content:
          "Não foi possível atualizar os pagamentos selecionados.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    } else {
      if (cr.length === 0) {
        this.sendNotification({
          type: "success",
          content: "Não foi selecionada nenhuma transação ."
        });
      }
      this.sendNotification({
        type: "success",
        content: "Transações conciliadas com sucesso."
      });
    }

    this.props.onPendingTransactionsClose();
    this.enqueue({ loading: false });
  }

  onChange(receivable_account_id, payment_uuid) {
    this.enqueue(vm => {
      const receivableIndex = vm.receivables.findIndex(r => {
        const paymentsUuids = r.ambiguous_payments.map(ap => ap.payment.uuid);
        return paymentsUuids.includes(payment_uuid);
      });

      if (receivableIndex === -1) {
        return {};
      }

      const receivable = vm.receivables[receivableIndex];
      if (!receivable.linked_payments) {
        vm.receivables[receivableIndex].linked_payments = [];
      }

      const linkedPaymentIndex = receivable.linked_payments.findIndex(
        lp => lp.uuid === payment_uuid
      );
      if (linkedPaymentIndex === -1) {
        vm.receivables[receivableIndex].linked_payments.push({
          uuid: payment_uuid,
          selected: receivable_account_id,
          processed: false
        });
      } else {
        vm.receivables[receivableIndex].linked_payments[
          linkedPaymentIndex
        ].selected = receivable_account_id;
      }

      let checkedRowReceivableAccountId = false;

      if (receivable_account_id.length > 0) {
        checkedRowReceivableAccountId = true;
      }

      const checkedRows = Object.entries(vm.checkedRows).includes(payment_uuid)
        ? vm.checkedRows
        : {
            ...vm.checkedRows,
            ...{ [payment_uuid]: checkedRowReceivableAccountId }
          };
      return { receivables: vm.receivables, checkedRows };
    });
  }

  onChangeAll(receivable_account_id, selectedCheckBoxes) {
    if (selectedCheckBoxes.length === 0) {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Selecione ao menos um pagamento para conciliar com uma conta de recebível."
      });
    } else {
      const { payments } = this.viewModel;
      let receivable_account_name = "";
      let receivable_account_name_found = false;

      for (const ap of payments) {
        if (!!ap.accounts_found) {
          for (const af of ap.accounts_found) {
            if (parseInt(receivable_account_id, 10) === af.sale_account.id) {
              receivable_account_name = af.sale_account.name;
              receivable_account_name_found = true;
              break;
            }
          }
        }

        if (receivable_account_name_found) {
          break;
        }
      }

      this.sendNotification({
        type: "confirmation",
        title: "Você tem certeza?",
        content: `Tem certeza que deseja selecionar a conta de recebível ${receivable_account_name} para todos os pagamentos selecionados? Certifique-se de ter escolhido uma conta possível para todos os pagamentos selecionados.`,
        actions: [
          {
            label: "Cancelar"
          },
          {
            label: "Sim, eu tenho certeza",
            type: "accent",
            callback: () => {
              for (const cb of selectedCheckBoxes) {
                this.onChange(receivable_account_id, cb);
              }
            }
          }
        ]
      });
    }
  }

  onPageChange(page) {
    this.enqueue(
      vm => ({ pagination: { ...vm.pagination, page } }),
      () => this.checkPendingTransactions()
    );
  }

  onCheckBoxEnqueue(checkedRows) {
    this.enqueue({ checkedRows });
  }

  onSingleCheckBoxEnqueue(checkedRows) {
    this.enqueue(vm => ({
      checkedRows: {
        ...vm.checkedRows,
        ...checkedRows
      }
    }));
  }
}

export default MyFinancePendingTransactionsPresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/company/form/integrations/my-finance/pending-transactions/presenter.js